import { AuthMessageType, CirraServiceType } from '../enums/auth';
import { OrgEnvironmentType } from '../enums/organization';
import { AuthParams } from '../interfaces/organization';
import { createAxiosClient } from '../api/axiosClient';
import { OrgCredential } from '../interfaces/organization';
import { Notify } from './notificationService';
import { TokenService } from './secureTokenService';

interface FetchAuthParamsOptions {
  orgInfo?: OrgCredential | null;
  envType?: OrgEnvironmentType | undefined;
  service: CirraServiceType;
}
export const fetchAuthParams = async ({
  orgInfo = null,
  envType = undefined,
  service,
}: FetchAuthParamsOptions) => {
  const authClient = createAxiosClient({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    prefix: '/oauth2',
  });

  const headers: {
    organization_id: string;
    user_id: string;
    environment?: string;
    source_id?: string;
  } = { organization_id: '', user_id: '' };
  if (orgInfo) {
    headers['organization_id'] = orgInfo.organization_id;
    headers['user_id'] = orgInfo.user_id;
    // Set source_id header:
    // - For a clone (has source_id field): use the clone's source_id
    // - For a shared org the user doesn't own: use the org's id
    if (orgInfo.source_id) {
      // This is a clone of a shared org
      headers['source_id'] = orgInfo.source_id;
    } else if (
      orgInfo.is_shared &&
      orgInfo.cirra_user_id !== TokenService.getUserEmail()
    ) {
      // This is the original shared org
      headers['source_id'] = orgInfo.id;
    }
  }
  if (envType) {
    // The backend only accepts standard Salesforce environment types.
    // 'custom' is a frontend-only concept for custom domain orgs, which are production orgs.
    headers['environment'] =
      envType === OrgEnvironmentType.CUSTOM
        ? OrgEnvironmentType.PRODUCTION
        : envType;
  }

  const response = await authClient({
    url: `/sfdc/auth_params?service=${service}`,
    method: 'GET',
    headers: headers,
  });
  return response.data as AuthParams;
};

type OAuthUrlOptionsType = {
  authParams: AuthParams;
  url: string;
  loginHint?: string;
};
export const buildOAuthUrl = (opts: OAuthUrlOptionsType) => {
  const { authParams, url, loginHint } = opts;
  const queryArgs: Record<string, string> = {
    state: authParams.state,
    code_challenge: authParams.code_challenge,
    client_id: authParams.client_id,
    redirect_uri: authParams.redirect_uri,
    response_type: authParams.response_type,
    prompt: 'login',
  };
  if (loginHint) {
    queryArgs.login_hint = loginHint;
  }
  const queryString = new URLSearchParams(queryArgs).toString();
  return `${url}/services/oauth2/authorize?${queryString}`;
};

export interface OAuthSuccessResult {
  userId: string;
  organizationId: string;
  credentialId?: string;
}

export const handleOAuthResponse = (
  event: MessageEvent,
  onSuccess: (result: OAuthSuccessResult) => void,
  onError: (error: string) => void
): void => {
  if (event.data.type === AuthMessageType.SUCCESS) {
    window.removeEventListener('message', (e) =>
      handleOAuthResponse(e, onSuccess, onError)
    );
    onSuccess({
      userId: event.data.userId,
      organizationId: event.data.organizationId,
      credentialId: event.data.credentialId,
    });
  } else if (event.data.type === AuthMessageType.ERROR) {
    window.removeEventListener('message', (e) =>
      handleOAuthResponse(e, onSuccess, onError)
    );
    onError(event.data.error);
  }
};

export const createOAuthPopup = (
  url: string,
  messageListener: (event: MessageEvent) => void,
  onClose: () => void,
  width = 800,
  height = 600
) => {
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  const popup = window.open(
    url,
    'oauth-popup',
    `width=${width},height=${height},top=${top},left=${left}`
  );

  if (!popup) {
    Notify.warning('Popup failed to open. Please enable popups for this site.');
    return null;
  }

  popup.focus();
  window.addEventListener('message', messageListener);

  const interval = setInterval(() => {
    if (popup.closed) {
      clearInterval(interval);
      window.removeEventListener('message', messageListener);
      onClose();
    }
  }, 500);

  return popup;
};

export const pingOrg = async (cred: OrgCredential) => {
  console.debug(
    `Pinging organization ${cred.organization_id} / user ${cred.user_id}...`
  );
  const authClient = createAxiosClient({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    prefix: '/oauth2',
  });
  const response = await authClient({
    url: '/ping',
    method: 'GET',
    headers: {
      user_id: cred.user_id,
      organization_id: cred.organization_id,
      source_id: cred.is_shared ? null : cred.source_id,
    },
  });
  return response;
};
